import { generateId, isEqualWith } from "../libs/utils";
import { useUserStore } from "../libs/stores";
import { getStore } from "../libs/stores/base";

export function FormDataWrapper(formData, changeSet = {}) {
  return new Model(formData, changeSet);
}
function customComparator(currentValue, serverValue) {
  //If customComparator returns undefined, comparisons are handled by the isEqualWith method itself
  if (!currentValue || !serverValue) {
    //either of the values is undefined, let the isEqualWith method handle comparisons
    return;
  } else if (
    currentValue.hasOwnProperty("_id") &&
    serverValue.hasOwnProperty("_id") &&
    currentValue["_id"] === serverValue["_id"]
  ) {
    //for Lookup, Multiuser and User types, _id check is enough.
    return true;
  }
}
class Model {
  constructor(formData, changeSet) {
    this.dataSet = formData;
    this.id = formData._id || formData.Id;
    this.changeSet = changeSet;
    this.validation = {
      error: {},
      pendingSignatureUpload: []
    };
  }

  hasProperty(name) {
    return this.dataSet.hasOwnProperty(name);
  }

  getValue(name) {
    if (name === "_current_user") {
      // eslint-disable-next-line no-unused-vars
      const useFormStore = getStore("PreviewForm");
      let previewImpersonatedUser = useFormStore.getState().currentUser;
      let userDetail = previewImpersonatedUser
        ? previewImpersonatedUser
        : useUserStore.getState().currentUser;
      return { _id: userDetail._id, Name: userDetail.Name };
    }
    let val = this.dataSet[name];
    let currentSet = this.changeSet[this.id];
    if (currentSet && currentSet.hasOwnProperty(name)) {
      return currentSet[name];
    }
    return val || val === 0 || val === false ? val : "";
  }

  getList(childName) {
    let childChangeSet = this.getChildChangeSet(childName);

    let val = this.dataSet["Table::" + childName] || [];
    let childList = [];
    for (let childDict of val) {
      //remove the deleted entry from child list
      if (childChangeSet.D.indexOf(childDict._id) === -1) {
        childList.push(FormDataWrapper(childDict, childChangeSet.U));
      }
    }

    //add the newly created entry to childlist
    for (let id in childChangeSet.C) {
      let childObj = FormDataWrapper(childChangeSet.C[id], childChangeSet.U);
      childList.splice(childChangeSet.C[id]._index, 0, childObj);
    }

    //move the drag and dropped rows
    for (let id in childChangeSet.M) {
      //remove the item to be moved from the childList
      let indexOfTheItem = childList.findIndex((item) => {
        return item.id === id;
      });
      if (indexOfTheItem > -1) {
        let itemToBeMoved = childList.splice(indexOfTheItem, 1)[0];
        childList.splice(childChangeSet.M[id]._index, 0, itemToBeMoved);
      }
    }
    return childList;
  }

  update(name, value) {
    let currentSet = this.getCurrentSet();
    //incase of values from Grid, its setting "" for date instead of null
    //this change means in KF 3.0 all "" are null
    currentSet[name] = value !== "" ? value : null;
  }

  updateError(name, value) {
    let error = this.validation.error;
    if (
      value === undefined ||
      value === "" ||
      value === false ||
      (Array.isArray(value) && value.isEmpty())
    ) {
      Reflect.deleteProperty(error, name);
    } else {
      error[name] = value;
    }
  }

  updateSignatureUploadStatus(fieldId, status) {
    let pendingSignature = this.validation.pendingSignatureUpload;
    if (status === "Done") {
      pendingSignature.pop(fieldId);
    } else {
      pendingSignature.push(fieldId);
    }
  }

  updateMulti(args = {}) {
    for (let name in args) {
      let value = args[name];
      this.update(name, value);
    }
  }

  move(childName, child, insertAfter) {
    let childChangeSet = this.getChildChangeSet(childName);
    childChangeSet.M[child.id] = { _index: insertAfter };
    /*
    childList.reverse().map((child) => {
      childChangeSet.M[child.id] = { _index: insertAfter };
    });
    */
  }

  create(childName, insertAfter = -1, params = {}) {
    let childChangeSet = this.getChildChangeSet(childName);
    let childId = generateId(childName);
    if (insertAfter === -1) {
      /* when adding more than 1 row at a time, we need to get the latest data
      for which we are using getList instead of this.dataSet*/
      let existingChildList = this.dataSet["Table::" + childName] || [];
      let newlyCreatedListLength = Object.keys(childChangeSet.C).length;
      insertAfter = existingChildList.length + newlyCreatedListLength;
    }
    let args = { _id: childId, _index: insertAfter };
    childChangeSet.C[childId] = args;
    let child = FormDataWrapper(args, childChangeSet.U);
    child.updateMulti(params);
    return child;
  }

  delete(childName, id) {
    let childChangeSet = this.getChildChangeSet(childName);
    if (childChangeSet.C.hasOwnProperty(id)) {
      Reflect.deleteProperty(childChangeSet.C, id);
      if (childChangeSet.U.hasOwnProperty(id)) {
        Reflect.deleteProperty(childChangeSet.U, id);
      }
    } else {
      childChangeSet.D.push(id);
    }
    if (childChangeSet.U.hasOwnProperty(id)) {
      Reflect.deleteProperty(childChangeSet.U, id);
    }
  }

  deleteMulti(childName, ids) {
    for (let i = 0; i < ids.length; i++) {
      this.delete(childName, ids[i]);
    }
  }

  getCurrentSet() {
    let currentSet = this.changeSet[this.id];
    if (!currentSet) {
      currentSet = {};
      this.changeSet[this.id] = currentSet;
    }
    return currentSet;
  }

  getChildChangeSet(childName) {
    let currentSet = this.getCurrentSet();
    let name = "Table::" + childName;
    let childChangeSet = currentSet[name];
    if (!currentSet[name]) {
      childChangeSet = { C: {}, U: {}, D: [], M: {} };
      currentSet[name] = childChangeSet;
    }
    return childChangeSet;
  }

  getChangeSummary() {
    let changeSet = this.changeSet[this.id];
    if (changeSet && Object.keys(changeSet).length > 0) {
      let changeSummary = { _id: this.id };
      for (let key in changeSet) {
        let val = changeSet[key];
        if (key.indexOf("::") === 5) {
          let existingchildList = this.dataSet[key] || [];
          let existingChildIds = existingchildList.map((childDict) => {
            return childDict._id;
          });
          let childSummary = [];
          changeSummary[key] = childSummary;
          for (let childId in val.C || {}) {
            let childDict = val.C[childId];
            childDict["_id"] = childId;
            childDict["_is_created"] = true;
            if (
              childDict["_index"] > 0 &&
              existingChildIds.length > childDict["_index"]
            ) {
              childDict["_previous_id"] =
                existingChildIds[childDict["_index"] - 1];
            } else if (childDict["_index"] > 0) {
              //update the last previous id to all newly created childs
              childDict["_previous_id"] =
                existingChildIds[existingChildIds.length - 1];
            }
            //incase of created item is updated, we need to send it as on entry in changesummary
            let updatedDict = val.U[childId];
            if (updatedDict) {
              Object.assign(childDict, updatedDict);
            }
            childSummary.push(childDict);
          }

          for (let childId in val.U || {}) {
            //since we merge the update dict part of creatdict,we need to skip the updatedict incase
            if (!val.C[childId]) {
              let childDict = val.U[childId];
              childDict["_id"] = childId;
              childSummary.push(childDict);
            }
            // here also we can update the Update dict when the fieldid present in Delete Dict
          }

          for (let childId of val.D || []) {
            childSummary.push({ _id: childId, _is_deleted: true });
          }

          for (let childId in val.M || {}) {
            let childDict = val.M[childId];
            childDict["_id"] = childId;
            childDict["_is_moved"] = true;
            if (childDict["_index"] > 0) {
              childDict["_previous_id"] =
                existingChildIds[childDict["_index"] - 1];
            }
            childSummary.push(childDict);
          }
        } else {
          changeSummary[key] = val;
        }
      }
      return changeSummary;
    }
  }

  merge(summaryData) {
    for (let key in summaryData) {
      let val = summaryData[key];
      if (key.indexOf("::") === 5) {
        this.previousIds = {};
        for (let i in val || []) {
          this.mergeTableData(key, val[i]);
        }
      } else {
        this.dataSet[key] = val;
        //remove the key from changeSet after merging
        if (
          this.changeSet[this.id] &&
          isEqualWith(this.changeSet[this.id][key], val, customComparator)
        ) {
          Reflect.deleteProperty(this.changeSet[this.id], key);
        }
      }
    }
  }

  mergeTableData(tableName, childDict) {
    let dirtyGraph = this.changeSet[this.id];
    let existingchildList = this.dataSet[tableName];
    if (existingchildList == null) {
      existingchildList = [];
      this.dataSet[tableName] = existingchildList;
    }
    let existingChildIds = existingchildList.map((childDict) => {
      return childDict._id;
    });
    let childId = childDict._id;
    let itemIndex = existingChildIds.indexOf(childId);
    if (itemIndex === -1) {
      //Newly created
      //Incase for grid _index will not get sent in changesummary
      //Also incase of grid if we insert above the row 1, the previous id will also be null
      //so setting the default value of insertIndex to 0 to insert it in the first position(above first row)
      let insertIndex = 0; //existingChildIds.length - 1;
      if (childDict._previous_id) {
        insertIndex = existingChildIds.indexOf(childDict._previous_id) + 1;
        //incase of continous add 5 or 10 rows at the bottom previous id will be same for all the 5 or 10 rows
        //due to same previous id, while merging [2,3,4,5,6] after 1 it will get reversed like [1,6,5,4,3,2]
        //so tmp maintaining a dict to increment the insertindex to get [1,2,3,4,5,6]
        if (this.previousIds.hasOwnProperty(childDict._previous_id)) {
          this.previousIds[childDict._previous_id] =
            this.previousIds[childDict._previous_id] + 1;
          insertIndex = insertIndex + this.previousIds[childDict._previous_id];
        } else {
          this.previousIds[childDict._previous_id] = 0;
        }
      } else if (childDict._index) {
        insertIndex = childDict._index;
      }
      existingchildList.splice(insertIndex, 0, childDict);
      existingChildIds.splice(insertIndex, 0, childId);

      //remove the childId from C changeset after merging
      Reflect.deleteProperty(dirtyGraph[tableName].C, childId);
      //remove the childId from U incase of createudpate after merging
      var updateDirtyDict = dirtyGraph[tableName].U[childId];
      if (updateDirtyDict) {
        //incase of removing childId from U, we need to only remove the fields which are part of server response
        for (let key in updateDirtyDict) {
          if (childDict.hasOwnProperty(key)) {
            Reflect.deleteProperty(updateDirtyDict, key);
          }
        }
        //incase of child table creation, we will create one default U graph
        //on merging if we the update graph U is empty then we are removing it
        if (Object.keys(updateDirtyDict).length === 0) {
          Reflect.deleteProperty(dirtyGraph[tableName].U, childId);
        }
      }
    } else if (childDict._is_deleted === true) {
      //Item got deleted
      existingchildList.splice(itemIndex, 1);
      existingChildIds.splice(itemIndex, 1);

      //remove the merged entry from changeset
      dirtyGraph[tableName].D.splice(0, 1);
    } else {
      //Item got updated
      let existingChildDict = existingchildList[itemIndex];
      let childDirtyGraph =
        (dirtyGraph[tableName] && dirtyGraph[tableName].U[childId]) || {};

      //Object.assign(existingChildDict, childDict);
      for (let key in childDict) {
        if (
          key !== "_id" &&
          isEqualWith(childDirtyGraph[key], childDict[key], customComparator)
        ) {
          Reflect.deleteProperty(childDirtyGraph, key);
        }
        existingChildDict[key] = childDict[key];
      }

      //clear the update dict after merging;
      if (Object.keys(childDirtyGraph).length === 1) {
        Reflect.deleteProperty(dirtyGraph[tableName].U, childId);
      }
      let newIndex = existingChildDict._index;
      if (existingChildDict._previous_id) {
        newIndex = existingChildIds.indexOf(existingChildDict._previous_id) + 1;
      }
      if (newIndex !== null && newIndex !== undefined) {
        Reflect.deleteProperty(existingChildDict, "_index");
        Reflect.deleteProperty(existingChildDict, "_previous_id");

        if (itemIndex < newIndex) {
          existingchildList.splice(newIndex, 0, existingChildDict);
          existingchildList.splice(itemIndex, 1);
        } else if (itemIndex > newIndex) {
          existingchildList.splice(itemIndex, 1);
          existingchildList.splice(newIndex, 0, existingChildDict);
        }
        //clear the moved dict after merging
        Reflect.deleteProperty(dirtyGraph[tableName].M, childId);
      }
    }
  }

  getError(name) {
    let error = this.validation.error;
    return name ? error[name] : error;
  }

  getUploadStatus() {
    return this.validation.pendingSignatureUpload;
  }
}
